import React, { useEffect, useState } from "react";

import api from "../services/api";
import TransactionStatusBadge from "../components/TransactionStatusBadge";

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
};

const signForType = (type = "") => {
  const normalized = type.toLowerCase();
  if (normalized.includes("withdraw") || normalized.includes("transfer out")) {
    return -1;
  }
  return 1;
};

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await api.getHistory();
        const list = (Array.isArray(data) ? data : []).map((txn, idx) => ({
          id: txn.id ?? idx,
          type: txn.type || txn.transaction_type || "-",
          amount: Number(txn.amount || 0) * signForType(txn.type || txn.transaction_type),
          note: txn.note || txn.asset || "",
          timestamp: txn.timestamp || txn.created_at || txn.date || null,
          status: txn.status || "successful",
          statusLabel: txn.status_label,
          statusHelp: txn.status_help,
          affectsBalance: txn.affects_balance !== false,
        }));
        setTransactions(list);
      } catch (err) {
        console.error("transactions", err);
        setError(err.message || "Unable to load transactions.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const downloadStatement = async () => {
    try {
      setDownloading(true);
      setError("");
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const blob = await api.downloadStatement(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ecoops-statement-${startDate || "from"}-${endDate || "to"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("statement", err);
      setError(err.message || "Unable to download statement.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Transaction history</h1>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            />
            <span className="text-gray-500 text-sm">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            />
            <button
              onClick={downloadStatement}
              disabled={downloading}
              className="bg-indigo-600 text-white text-sm px-3 py-2 rounded hover:bg-indigo-700 disabled:opacity-60"
            >
              {downloading ? "Preparing PDF..." : "Download statement"}
            </button>
          </div>
        </div>
        <div className="bg-white rounded-lg p-6 shadow">
          <p className="text-sm text-gray-500 mb-4">
            Review posted wallet movements and pending requests waiting for review.
          </p>

          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : error ? (
            <p className="text-red-600 text-sm">{error}</p>
          ) : !transactions.length ? (
            <p className="text-gray-500">No transactions recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="px-4 py-2">Type</th>
                    <th className="px-4 py-2">Amount</th>
                    <th className="px-4 py-2">Note</th>
                    <th className="px-4 py-2">Timestamp</th>
                    <th className="px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transactions.map((txn) => (
                    <tr key={txn.id}>
                      <td className="px-4 py-3 capitalize">{txn.type}</td>
                      <td className="px-4 py-3 font-medium">{formatCurrency(txn.amount)}</td>
                      <td className="px-4 py-3">{txn.note || "-"}</td>
                      <td className="px-4 py-3 text-gray-500">
                        {txn.timestamp ? new Date(txn.timestamp).toLocaleString() : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <TransactionStatusBadge status={txn.status} label={txn.statusLabel} />
                        {!txn.affectsBalance && (
                          <p className="mt-1 text-xs text-gray-500">
                            {txn.statusHelp || "Not posted yet"}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transactions;
